import {
  Box,
  Button,
  Link as ChakraLink,
  HStack,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import get from "lodash/get"
import { useEffect } from "react"
import { flattenEdges } from "../../utils/apollo"
import CardTemplate from "./CardTemplate"
import {
  PublishSuccess,
  SourcePublishSuccess,
  SyncSuccess
} from "./SuccessCard"
import SyncCardChakraProvider from "./SyncCardChakraProvider"
import SyncCardProvider, { useSyncCard } from "./SyncCardProvider"
import {
  useCohortSyncPlanContext,
  useCohortSyncPlanQuery
} from "./SyncPlanContext"
import { SYNC_DIR_BACKWARD, SYNC_DIR_FORWARD } from "./constants"
import { useSelectedItemSourceIds, useSyncDir } from "./hooks"
import { SelectedSourceIDs, SyncDirection, SyncPlanContext } from "./types"
import {
  getPotentiallySyncableChangesCount,
  useShouldShowTips,
  useShowSyncOption
} from "./utils"

const Publishable = () => {
  const { fromCohort, syncDir, cohortName, isSourcePath } = useSyncCard()

  const { tc } = useTranslate()

  const cohorts = flattenEdges(fromCohort.resource?.cohorts) || []

  const description =
    syncDir === SYNC_DIR_BACKWARD
      ? tc(
          "Sync changes from the {{cohortName}} Cohort with the Source Path to publish them to all Cohorts.",
          {
            cohortName: cohortName
          }
        )
      : isSourcePath
      ? tc("Publish changes to all {{cohortCount}} Cohorts.", {
          cohortCount: cohorts.length
        })
      : tc("Publish changes to {{cohortName}} Cohort.", {
          cohortName: cohortName
            ? cohortName
            : cohorts.length > 1
            ? tc("all cohorts")
            : cohorts[0]?.name
        })

  const title = tc(
    syncDir === SYNC_DIR_BACKWARD ? "Sync to Source Path" : "Draft"
  )

  return (
    <CardTemplate
      title={title}
      description={description}
      enableButton
      showMeta
      allowShowSync
      icon={
        syncDir === SYNC_DIR_BACKWARD
          ? "cloud-arrows-fill"
          : "cloud-notification-fill"
      }
    />
  )
}
const Unpublishable = () => {
  const { cohortName } = useSyncCard()
  const { tc } = useTranslate()

  return (
    <CardTemplate
      title={tc("Draft")}
      allowShowSync
      description={tc(
        "To publish the {{cohortName}} Path, add and publish Blocks content for at least one item below.",
        {
          cohortName: cohortName
        }
      )}
      showMeta
      icon="cloud"
    />
  )
}

const EmptyPath = () => {
  const { cohortName, fromCohort } = useSyncCard()

  const { tc } = useTranslate()

  const isCollection = fromCohort?.resource?.resource_type === 30

  return (
    <CardTemplate
      title={tc("Draft")}
      description={
        isCollection
          ? tc(
              "To publish the {{cohortName}} Collection, add at least one Path.",
              {
                cohortName: cohortName
              }
            )
          : tc(
              "To publish the {{cohortName}} Path, add at least one Lesson, Step, and Blocks content.",
              {
                cohortName: cohortName
              }
            )
      }
      showMeta
      icon="cloud"
    />
  )
}

const PathPublishLoading = () => {
  const { tc } = useTranslate()

  return (
    <CardTemplate
      title={`${tc("Loading")}...`}
      description={`${tc("Loading Path Publish")}...`}
      renderBody={() => (
        <HStack justify="center" w="full" py={10}>
          <Spinner />
        </HStack>
      )}
      icon="cloud-arrows-fill"
    />
  )
}

const PathPublishError = () => {
  const { onClose, cohortName, error } = useSyncCard()
  const { tc, t } = useTranslate()

  return (
    <Box
      sx={{
        ".SyncCard__card-wrapper": {
          paddingBottom: "var(--chakra-space-2)"
        },
        ".SyncCard__card-button-wrapper": {
          paddingTop: "var(--chakra-space-1)"
        }
      }}
    >
      <CardTemplate
        icon="cloud-x-fill"
        title={tc("Error")}
        description={tc("Error loading publish for {{cohortName}} Path.", {
          cohortName: cohortName
        })}
        renderButton={() => (
          <Button colorScheme="brand" cursor="pointer" onClick={onClose}>
            {tc("Close")}
          </Button>
        )}
        renderBody={() => (
          <VStack w="md" align="center" pt={10} spacing={0} m="0 auto">
            <Text
              w="full"
              mt={0}
              p={0}
              overflowX="auto"
              fontSize="sm"
              fontFamily="mono"
              color="gray.800"
              bg="gray.50"
            >
              {error?.message}
            </Text>
            <Text mb={0}>
              {tc("You can try again later or")}{" "}
              <ChakraLink
                textDecoration="underline"
                href="mailto:hello@pathwright.com"
              >
                {t("contact support")}
              </ChakraLink>
              .
            </Text>
          </VStack>
        )}
        renderTip={() => null}
      />
    </Box>
  )
}

const SyncCard = () => {
  const { syncPlan, syncDir, items, error, changes, isSourcePath, ...rest } =
    useSyncCard()

  const numPathItems = items?.filter(
    // Basically, the only item that cannot indicate a published path is a lesson.
    (item) =>
      item.type === "step" ||
      item.type === "resource" ||
      item.type === "divider"
  )?.length

  const isEmptyPath = !numPathItems || numPathItems === 0
  const isSuccessfulPublish = syncPlan?.mutation?.data

  const unsyncable = [
    ...changes?.stepsDraftBlocks,
    ...changes?.stepsNoContent,
    ...changes?.stepsUnpublishedBlocks,
    ...changes?.lessonsNoSteps,
    ...changes?.lessonsNoValidSteps
  ]

  const potentiallySyncableChangesCount =
    getPotentiallySyncableChangesCount(syncPlan)

  const isUnpublishable =
    !!numPathItems && !potentiallySyncableChangesCount && !unsyncable?.length

  const isPublished =
    !!numPathItems && !potentiallySyncableChangesCount && !unsyncable?.length

  const isPublishable = !!potentiallySyncableChangesCount

  if (error) return <PathPublishError />

  if (isSuccessfulPublish || isPublished) {
    return syncDir === SYNC_DIR_BACKWARD ? (
      <SyncSuccess />
    ) : isSourcePath ? (
      <SourcePublishSuccess />
    ) : (
      <PublishSuccess />
    )
  }

  // Instead, we'll show loading in specific components (ex: button, publish icon)
  // if (loading) return <PathPublishLoading />

  if (isEmptyPath) return <EmptyPath />

  if (isUnpublishable) return <Unpublishable />

  // Published and success are now the same
  // if (isPublished) return <IsPublished />

  if (isPublishable) return <Publishable />

  return <Unpublishable />
}

type SyncCardProps = {
  cohortId: number
  syncDir: SyncDirection
  onCreateCohort: () => void
  toCohortId?: number
  onClickStep?: (stepId: number) => void
  onClose?: () => void
  defaultSelectedItemSourceIDs?: SelectedSourceIDs
}

const SyncCardContainer = ({
  cohortId,
  // If set to SYNC_DIR_BACKWARD, forces SyncCard to allow back sync
  // Otherwise, determined by useShowSyncOption
  syncDir: syncDirProp = SYNC_DIR_FORWARD,
  onCreateCohort,
  onClose,
  onClickStep,
  defaultSelectedItemSourceIDs
}: SyncCardProps) => {
  const {
    fromCohort,
    toCohort,
    query: cohortSyncPlanQuery
  } = useCohortSyncPlanQuery({
    cohortId
  })

  const backSyncIsAvailable = useShowSyncOption({
    cohortId
  })

  // Force allow back sync via prop syncDir, or if back sync is available
  const allowBackSync = syncDirProp === SYNC_DIR_BACKWARD || backSyncIsAvailable

  const [syncDir, setSyncDir] = useSyncDir({
    cohortId,
    syncDir: syncDirProp
  })

  const [
    selectedItemSourceIds,
    toggleSelectedItemSourceId,
    setSelectedItemSourceIds
  ] = useSelectedItemSourceIds({
    cohortId,
    syncDir,
    defaultSelectedItemSourceIDs,
    allowBackSync
  } as {
    cohortId: number
    syncDir: SyncDirection
    defaultSelectedItemSourceIDs?: SelectedSourceIDs
    allowBackSync?: boolean
  })

  const syncPlanContext = useCohortSyncPlanContext({
    cohortId,
    selectedItemSourceIds,
    allowBackSync
  })

  const { refetchSyncPlanQueries } = syncPlanContext
  const syncPlan =
    syncDir === SYNC_DIR_FORWARD
      ? syncPlanContext.forward
      : syncPlanContext.backward

  const { fromPath } = syncPlanContext

  // If a Designer is "new" to the sync card, we want the Tips to default to expanded
  // We consider the Cohort "new" if neither the Cohort or Source Path have been published AND
  // there is only 1 Cohort
  const shouldExpandTips = useShouldShowTips({
    resourceId: fromCohort?.resource?.id,
    cohortId,
    sourceCohortId: toCohort?.id || fromCohort?.source?.id
  })

  useEffect(() => {
    // always refetch the sync plan queries on mount to ensure we have the current hash and correct no. of Path Items
    refetchSyncPlanQueries()
    cohortSyncPlanQuery.refetch()
  }, [])

  const error = get(syncPlan, "query.error") || get(syncPlan, "mutation.error")

  return (
    <SyncCardChakraProvider>
      <SyncCardProvider
        onCreateCohort={onCreateCohort}
        onClose={onClose}
        onClickStep={onClickStep}
        error={error}
        loading={!syncPlanContext || !syncPlan || !fromPath}
        toCohort={toCohort}
        fromCohort={fromCohort}
        syncPlanContext={syncPlanContext as SyncPlanContext}
        fromPath={fromPath}
        syncDir={syncDir}
        onSetSyncDir={(syncDir: SyncDirection) => {
          setSyncDir(syncDir)
        }}
        onPublish={async () => {
          await syncPlan?.mutation?.mutate()
          await refetchSyncPlanQueries()
        }}
        selectedItemSourceIds={selectedItemSourceIds}
        toggleSelectedItemSourceId={toggleSelectedItemSourceId}
        setSelectedItemSourceIds={
          setSelectedItemSourceIds as React.Dispatch<
            React.SetStateAction<SelectedSourceIDs>
          >
        }
        shouldExpandTips={shouldExpandTips}
      >
        <SyncCard />
      </SyncCardProvider>
    </SyncCardChakraProvider>
  )
}

export default SyncCardContainer
